import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { signIn } from '@/lib/services/auth'
import { routes } from '@/lib/config'
import { getAuthErrorMessage } from '@/lib/errorMessages'
import { updateLastAccess } from '@/lib/services/profiles'
import { AuthLayout } from '@/components/auth'
import { Button, Alert, LanguageSelector } from '@/components/ui'
import { EmailIcon, LockIcon, EyeIcon, EyeOffIcon } from '@/components/ui/Icons'
import { useTranslation } from '@/contexts/TranslationContext'
import { useTheme } from '@/contexts/ThemeContext'

// Shared styles
const getInputClasses = (isDark: boolean) => {
  const base = `block w-full rounded-lg border-0 py-3 shadow-sm ring-1 ring-inset focus:ring-2 sm:text-sm ${
    isDark
      ? 'bg-slate-700 text-slate-100 ring-slate-600 placeholder:text-slate-400 focus:ring-emerald-500'
      : 'bg-white text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-green-600'
  }`
  return base
}

export default function Login() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const INPUT_BASE_CLASS = getInputClasses(isDark)
  const INPUT_WITH_ICONS_CLASS = `${INPUT_BASE_CLASS} pl-10 pr-3`
  const INPUT_WITH_BOTH_ICONS_CLASS = `${INPUT_BASE_CLASS} pl-10 pr-10`

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const { data, error } = await signIn(email, password)

      if (error) throw error

      // Atualizar informações de último acesso
      if (data.user) {
        await updateLastAccess(data.user.id)
      }

      // Sempre redirecionar para dashboard
      // O dashboard verificará se o usuário tem units e redirecionará para onboarding se necessário
      navigate(routes.dashboard)
    } catch (error: any) {
      setError(getAuthErrorMessage(error.message, t))
    } finally {
      setIsLoading(false)
    }
  }

  const handleTogglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev)
  }

  return (
    <AuthLayout subtitle={t('auth.loginSubtitle')}>
      {/* Seletor de Idioma */}
      <div className="mb-6 flex justify-end">
        <LanguageSelector />
      </div>

      <form onSubmit={handleLogin} className="space-y-4">
        {/* Campo Email */}
        <div>
          <label htmlFor="email" className={`mb-2 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
            {t('auth.email')}
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <EmailIcon />
            </div>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className={INPUT_WITH_ICONS_CLASS}
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Campo Senha */}
        <div>
          <label htmlFor="password" className={`mb-2 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
            {t('auth.password')}
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <LockIcon />
            </div>
            <input
              id="password"
              name="password"
              type={isPasswordVisible ? 'text' : 'password'}
              autoComplete="current-password"
              required
              className={INPUT_WITH_BOTH_ICONS_CLASS}
              placeholder={t('auth.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={handleTogglePasswordVisibility}
              aria-label={isPasswordVisible ? t('auth.hidePassword') : t('auth.showPassword')}
            >
              {isPasswordVisible ? (
                <EyeOffIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mensagem de erro */}
        {error && (
          <Alert variant="error">
            {error}
          </Alert>
        )}

        {/* Botão Entrar */}
        <Button type="submit" fullWidth loading={isLoading}>
          {t('auth.loginButton')}
        </Button>
      </form>

        {/* Esqueceu a senha */}
      <p className="mt-4 text-center text-sm">
        <Link
          to={routes.forgotPassword}
          className="font-semibold text-green-600 hover:text-green-700"
        >
          {t('auth.forgotPassword')}
        </Link>
      </p>

        {/* Link de cadastro */}
      <p className="mt-3 text-center text-sm text-gray-600">
        {t('auth.noAccount')}{' '}
        <Link
          to={routes.signup}
          className="font-semibold text-green-600 hover:text-green-700"
        >
          {t('auth.signupLink')}
        </Link>
      </p>
    </AuthLayout>
  )
}
